import React, { useEffect, useRef, useState } from "react";
import "./styles/NavDropdown.css";
import "./styles/Navbar.css"
import { Link } from "react-router-dom";

export type NavDropdownItem = {
  label: string;
  href: string;
  target?: string;
};

type NavDropdownProps = {
  label: string;
  items: NavDropdownItem[];
  activeSection: string;
  align?: "left" | "right";
  className?: string;
  setActiveSection: () => void
};

const NavDropdown: React.FC<NavDropdownProps> = ({
  label,
  items,
  activeSection,
  align = "left",
  className,
  setActiveSection
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const firstItemRef = useRef<HTMLAnchorElement | null>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      firstItemRef.current?.focus({preventScroll: true});
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleKeyDownTrigger = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(true);
    }
  };

  const handleKeyDownMenu = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  };

  return (
    <div
      className={`nav-dropdown-root ${className ?? ""}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`nav-dropdown-trigger ${
          open ? "nav-dropdown-trigger--open" : ""
        }
        ${activeSection.startsWith('developers') ? 'active' : ''}
        `}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={handleToggle}
        onKeyDown={handleKeyDownTrigger}
      >
        <span className={`nav-dropdown-label`}>{label}</span>
        <span className={`nav-dropdown-chevron ${open ? "is-open" : ""}`}>
          ▾
        </span>
      </button>

      <div
        className={`nav-dropdown-menu nav-dropdown-menu--align-${align} ${
          open ? "nav-dropdown-menu--open" : ""
        }`}
        role="menu"
        aria-hidden={!open}
        onKeyDown={handleKeyDownMenu}
      >
        {items.map((item, index) => (
          <Link
            key={item.href + item.label}
            to={item.href}
            target={item.target}
            className={`nav-dropdown-item`}
            role="menuitem"
            ref={index === 0 ? firstItemRef : undefined}
            onClick={() => {
                setOpen(false);
                setActiveSection();
            }}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

export default NavDropdown;
